import React from 'react';
import { Helmet } from 'react-helmet-async';
import Stats from '../../../components/Global/BannerCard/Stats/Stats';
import LIRC from '../../../components/Global/BannerCard/LIRC/LIRC';
import ExpertiseSection from '../../../components/Global/BannerCard/Expertise/ExpertiseSection';
import THC from '../../../components/Global/BannerCard/T&HC/THC';
import MakeTheShift from '../../../components/MakeTheShift/MakeTheShift';
import CyientGrid from '../../../components/Global/BannerCard/CyientGrid/CyientGrid';
import ContactPage from '../../../components/Global/BannerCard/DynamicHeader/ContactPage1';

const Communications = () => {
  const canonicalUrl = `${process.env.REACT_APP_BASE_URL}/communications`;
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <>
      <Helmet>
        <html lang="en" />
        <meta charSet="utf-8" />
        <title>Network Communications Solutions | V Adithya Infinity Pvt Ltd</title>
        <meta
          name="description"
          content="Empowering autonomous operations and driving digital transformation in network infrastructure. Discover our end-to-end communications engineering services."
        />
        <meta
          name="keywords"
          content="Network communications India, autonomous operations, telecom solutions, infrastructure engineering, V Adithya Infinity, digital networks, connectivity services, 5G implementation, network automation"
        />
        {isDevelopment ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <link rel="canonical" href={canonicalUrl} />
        )}

        {/* Open Graph */}
        <meta property="og:title" content="Network Communications Solutions | V Adithya Infinity Pvt Ltd" />
        <meta
          property="og:description"
          content="Enabling network transformation and empowering autonomous operations with expert telecom and digital infrastructure solutions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:image"
          content="https://www.adithyainfinity.com/assets/images/communications-banner.jpg"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Network Communications Solutions | V Adithya Infinity Pvt Ltd" />
        <meta
          name="twitter:description"
          content="Discover network transformation and connectivity services from V Adithya Infinity Pvt Ltd. Telecom, automation, and beyond."
        />
        <meta
          name="twitter:image"
          content="https://www.adithyainfinity.com/assets/images/communications-banner.jpg"
        />

        {/* JSON-LD: Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "V Adithya Infinity Pvt Ltd",
            url: canonicalUrl,
            logo: "https://www.adithyainfinity.com/assets/images/logo.png",
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+91-9876543210",
              contactType: "Customer Service",
              areaServed: "IN",
              availableLanguage: ["English", "Hindi", "Telugu"]
            },
            sameAs: [
              "https://www.linkedin.com/company/vadithyainfinity",
              "https://www.facebook.com/vadithyainfinity"
            ]
          })}
        </script>

        {/* JSON-LD: Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Network Communications and Autonomous Operations",
            provider: {
              "@type": "Organization",
              name: "V Adithya Infinity Pvt Ltd",
              url: "https://www.adithyainfinity.com",
              logo: "https://www.adithyainfinity.com/assets/images/logo.png"
            },
            areaServed: {
              "@type": "Country",
              name: "India"
            },
            description:
              "Comprehensive solutions for network communications, infrastructure transformation, and autonomous telecom operations across India."
          })}
        </script>
      </Helmet>

      <div>
        <ContactPage
          title="NETWORK COMMUNICATIONS"
          subTitle="Enabling Network Transformation and Empowering Autonomous Operations"
          backgroundImage="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBw8NDw0NDQ0QDQ0NEBINDQ0NEBANDQ0NFRIWFhURFRUYHSggGBolGxUVITEhJSkrOi4uFx8/OD84Nyg5OisBCgoKDg0NFQ0NFSsZFRkrLS03LSsrKy03Ny0rKystLS03LS0rNy0tLS0tNysrLTc3Ny0tNystKy03Ny0tKy0tK//AABEIAKgBLAMBIgACEQEDEQH/xAAYAAEBAQEBAAAAAAAAAAAAAAAAAQIHA//EACYQAQAABQUAAgMBAQEAAAAAAAADMdHh8AIUQVOSARFRYcGhsSH/xAAVAQEBAAAAAAAAAAAAAAAAAAAAAf/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AOIAKKACoAigApZFsAAC3LJfktyC3Vm/LWTAuJfkr+QU+/8Av8TJrX8/oEVMmZMD7/gnxn/pkwLFyxcAABAARUAAFQBAAAAUURQAMmCiZMyYirbhMmW5BS3Bky3IBbgyZbkFvwJflcmBklyTOTWv5AyS1/jOTWv5/QGSEyZkwW3CZItyZMC3BfgtyX5AQyZkwATJgqGTBT5D5QABABAUAFEVQABRARSwW5BSxk0tyCljJluQLr9pfkyYH2qZMyYC5/iZMyf6ADJmTAsFuTJgWLluUvyCoZMyYAICoAoCIAAAICgAAAoCioAKBbkQWyZMyYKJk1yYFxL8rkwBMmuTAM/xMmuTAEya5MCwmTMmC5JDJmTADJgCAKAAICAACAAoAAAKIKKAClkAUQEUEBbqyoKIAKgAqAAAAgCoAoAAIAAIAIAioDQn2AoCgAgogCgKKgAomTAFQEUTJgKIuTAEyagCAAZMFAAEVEFQFAABPtUQQABftAGkAFAAABRAFEUAD7UFQAVAAABUAAAAQFQAAEAEBU+w+wPtAAAAABfg+EX4BRPgBRBRQEBUFFEEFEFFEEFEFFEEFEAAAAAAQFD7T7A+flFQAAAAAAAAFEAVWVBRFAAAAAAAAAAAAAAAAABAVBAUQAAAAABAURQAAAAAAFQBVZUFEAURfkARQBFAE+AFEAAAEAFQAAAAAAQAAHvs4vTE8aqGzi9MTxqoAGzi9MTxqouzi9MTxqogC7OL1RPGqhs4vTE8aqABs4vTE8aqGzi9MTxqoAGzi9MTxqobOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vVE8aqGzi9UTxqoAGzi9UTxqobOL1RPGqgAbOL1RPGqhs4vVE8aqABs4vVE8aqGzi9UTxqoAGzi9UTxqobOL1RPGqgAbOL1RPGqhs4vVE8aqABs4vTE8aqGzi9MTxqoAGzi9MTxqobOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vTE8aqGzi9UTxqogC7OL1RPGqibOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vTE8aqGzi9MTxqoAP/9k="
          highlightWord="COMMUNICATIONS"
        />
        <Stats />
        <LIRC />
        <CyientGrid />
        <ExpertiseSection />
        <THC />
        <MakeTheShift />
      </div>
    </>
  );
};

export default Communications;
